import React, { useState, useEffect, useRef, Fragment, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, Transition, Combobox } from '@headlessui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../config/firebase';
import { hasPageAccessSync, hasFeatureAccessSync } from '../utils/accessControl';
import { APP_TOP_BAR_LINKS } from '../config/permissionsRegistry';
import { 
  collection, 
  query, 
  where, 
  onSnapshot,
  getDocs,
  limit,
  doc,
  getDoc
} from 'firebase/firestore';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useLogo } from '../contexts/LogoContext';
import PageHandler from './PageHandler';
import { useFilteredNavMenuItems, isNavItemActive, SIDEBAR_GROUP_CONFIG } from './v2/navMenuConfig';
import { SERIF, SANS, todayTokens } from '../theme/todayTokens';
import { 
  FaSearch, 
  FaCog, 
  FaUserCircle, 
  FaSun,
  FaMoon,
  FaBars,
  FaChevronDown,
  FaUser,
  FaUserTie,
  FaBuilding,
  FaUsers,
  FaTools,
  FaPoundSign,
  FaChevronRight,
  FaShieldAlt,
  FaBell,
  FaFileDownload,
  FaFilter,
  FaCalendar,
  FaEnvelope,
  FaCertificate,
  FaCreditCard,
  FaHome,
  FaChartLine,
  FaArchive,
  FaLink,
  FaBullhorn,
  FaComments,
  FaClipboardCheck,
  FaTimes,
} from 'react-icons/fa';
import { isFeatureEnabled } from '../utils/featureFlags';
import { FEATURES } from '../features';
import '../styles/TopBarV2.css';
import GlobalSearchModal from './search/GlobalSearchModal';
import AvatarUserMenu from './topbar/AvatarUserMenu';
import {
  buildAutoSuggestionResults,
  getRecentSearches,
  addRecentSearch,
} from '../utils/searchSuggestions';

// ponytail: hard cap on unbounded collection scans; upgrade path = searchTerms prefix / array-contains
const SEARCH_SCAN_LIMIT = 400;

const TopBarV2 = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const filteredNavItems = useFilteredNavMenuItems();
  const [notificationCount, setNotificationCount] = useState(0);
  const { currentUser, logout, refreshUser, userRole, userPermissions } = useAuth();
  const { isDarkMode, toggleTheme, primaryColor } = useTheme();
  const { getDisplayLogo } = useLogo();
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const searchInputRef = useRef(null);
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const searchModalRef = useRef(null);
  const modalRef = useRef(null);
  const searchButtonRef = useRef(null);
  const searchRequestIdRef = useRef(0);
  // ponytail: session cache — one download per collection while modal is open; clear on close
  const searchSnapshotCacheRef = useRef(new Map());
  const lastUnfilteredSearchRef = useRef([]);
  const selectedCategoriesRef = useRef([]);
  const [localUserRole, setLocalUserRole] = useState(null);
  
  // Helper function to check page access using permissions from AuthContext
  const checkPageAccess = useCallback((pageName) => {
    if (!userRole || !pageName) return false;
    
    // Administrators and owners always have access
    const normalizedRole = userRole.toLowerCase();
    if (normalizedRole === 'administrator' || normalizedRole === 'owner') return true;
    
    // Check user permissions from context
    if (userPermissions?.pages && pageName in userPermissions.pages) {
      return userPermissions.pages[pageName] === true;
    }
    
    return false;
  }, [userRole, userPermissions]);

  // Helper function to check feature access using permissions from AuthContext
  const checkFeatureAccess = useCallback((featureName) => {
    if (!userRole || !featureName) return false;
    
    // Administrators and owners always have access
    const normalizedRole = userRole.toLowerCase();
    if (normalizedRole === 'administrator' || normalizedRole === 'owner') return true;
    
    // Check user permissions from context
    if (userPermissions?.features && featureName in userPermissions.features) {
      return userPermissions.features[featureName] === true;
    }
    
    return false;
  }, [userRole, userPermissions]);

  // Helper to check TopBar link visibility — mirrors sidebar logic:
  // visible if the user has page access OR the required feature is enabled.
  const checkTopBarLinkAccess = useCallback((linkId) => {
    if (!userRole || !linkId) return false;
    const normalizedRole = userRole.toLowerCase();
    if (normalizedRole === 'administrator' || normalizedRole === 'owner') return true;
    const linkDef = APP_TOP_BAR_LINKS.find(l => l.id === linkId);
    if (!linkDef) return false;
    const hasPageAccess = userPermissions?.pages?.[linkDef.pageName] === true;
    const hasFeatureAccess = linkDef.requiredFeature
      ? userPermissions?.features?.[linkDef.requiredFeature] === true
      : false;
    return hasPageAccess || hasFeatureAccess;
  }, [userRole, userPermissions]);
  const [hasRefreshed, setHasRefreshed] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(0);
  const [welcomeMessage, setWelcomeMessage] = useState('Nice to see you again');
  const [currentDate, setCurrentDate] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  selectedCategoriesRef.current = selectedCategories;
  const [showFilters, setShowFilters] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Convert hex color to RGB for opacity transformations
  const hexToRgb = (hex) => {
    // Remove the # if present
    hex = hex.replace('#', '');
    
    // Parse the hex values
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    
    // Return as RGB object
    return { r, g, b };
  };

  // Helper function to get user's first letter for avatar
  const getUserInitial = () => {
    if (!currentUser) return 'U';
    
    return (
      currentUser.displayName?.charAt(0).toUpperCase() || 
      currentUser.firstName?.charAt(0).toUpperCase() || 
      currentUser.lastName?.charAt(0).toUpperCase() || 
      currentUser.email?.charAt(0).toUpperCase() || 
      'U'
    );
  };

  // Categories for search
  const searchCategories = [
    { id: 'pages', name: 'Pages', icon: FaHome },
    { id: 'properties', name: 'Properties', icon: FaBuilding },
    { id: 'tenants', name: 'Tenants', icon: FaUsers },
    { id: 'maintenance', name: 'Maintenance', icon: FaTools },
    { id: 'finance', name: 'Finance', icon: FaPoundSign },
    { id: 'payments', name: 'Payments', icon: FaCreditCard },
    { id: 'landlords', name: 'Landlords', icon: FaUser },
    { id: 'users', name: 'Users', icon: FaUserTie },
    { id: 'links', name: 'Links', icon: FaChevronRight },
    { id: 'documents', name: 'Documents', icon: FaFileDownload },
    { id: 'certificates', name: 'Certificates', icon: FaCertificate },
    { id: 'calendar', name: 'Calendar', icon: FaCalendar },
    { id: 'messages', name: 'Messages', icon: FaEnvelope },
  ];

  // Define all available pages with their access requirements
  const allPages = [
    { title: 'Dashboard', path: '/dashboard', icon: FaHome, pageName: 'dashboard', requiredFeature: 'viewDashboard' },
    { title: 'Properties', path: '/properties', icon: FaBuilding, pageName: 'properties', requiredFeature: 'viewProperties' },
    { title: 'Tenants', path: '/tenants', icon: FaUsers, pageName: 'tenants', requiredFeature: 'viewTenants' },
    { title: 'Landlords', path: '/landlords', icon: FaUserTie, pageName: 'landlords', requiredFeature: 'viewLandlords' },
    { title: 'Compliance', path: '/compliance', icon: FaShieldAlt, pageName: 'properties', requiredFeature: 'viewProperties' },
    { title: 'Inspections', path: '/inspections', icon: FaClipboardCheck, pageName: 'inspections', requiredFeature: 'viewInspections' },
    { title: 'Maintenance', path: '/maintenance', icon: FaTools, pageName: 'maintenance', requiredFeature: 'viewMaintenance' },
    { title: 'Calendar', path: '/calendar', icon: FaCalendar, pageName: 'calendar', requiredFeature: 'viewCalendar' },
    { title: 'Finance', path: '/finance', icon: FaPoundSign, pageName: 'finance', requiredFeature: 'viewFinance' },
    { title: 'Reports', path: '/reports', icon: FaChartLine, pageName: 'reports', requiredFeature: 'viewReports' },
    { title: 'Links', path: '/links', icon: FaLink, pageName: 'links', requiredFeature: 'viewLinks' },
    { title: 'Company Docs', path: '/company-documents', icon: FaFileDownload, pageName: 'companyDocuments', requiredFeature: 'viewDocuments' },
    { title: 'Broadcast', path: '/broadcast', icon: FaBullhorn, pageName: 'broadcast', requiredFeature: 'sendBroadcasts' },
    { title: 'Messages', path: '/messages', icon: FaComments, pageName: 'messages', requiredFeature: 'sendMessages' },
    { title: 'Contractors', path: '/contractors', icon: FaTools, pageName: 'contractors', requiredFeature: 'viewContractors' },
    { title: 'Contractor messages', path: '/contractor-messages', icon: FaComments, pageName: 'contractors', requiredFeature: 'viewContractors' },
    { title: 'Archive', path: '/archive', icon: FaArchive, pageName: 'archive', requiredFeature: 'viewArchive', adminOnly: true },
    { title: 'Settings', path: '/settings', icon: FaCog, pageName: 'settings', requiredFeature: 'accessSettings' },
    { title: 'Profile', path: '/profile', icon: FaUserCircle, pageName: 'profile', requiredFeature: null },
    { title: 'Account Security', path: '/account-management', icon: FaShieldAlt, pageName: 'account', requiredFeature: null },
    { title: 'Notifications', path: '/notifications', icon: FaBell, pageName: 'notifications', requiredFeature: null },
  ];

  // Settings sections
  const settingsSections = [
    { title: 'Users', path: '/settings?tab=users', icon: FaUsers, pageName: 'users', requiredFeature: 'viewUsers' },
    { title: 'Roles & Permissions', path: '/settings?tab=roles', icon: FaShieldAlt, pageName: 'roles', requiredFeature: 'manageRoles' },
    { title: 'Branding', path: '/settings?tab=branding', icon: FaCog, pageName: 'branding', requiredFeature: 'manageBranding' },
    { title: 'Audit Logs', path: '/settings?tab=audit', icon: FaFileDownload, pageName: 'audit', requiredFeature: 'viewAuditLogs' },
    { title: 'System Configuration', path: '/settings?tab=system', icon: FaCog, pageName: 'system', requiredFeature: 'systemConfig' },
  ];

  useEffect(() => {
    if (!currentUser) return;

    const notificationsRef = collection(db, 'notifications');
    const notificationsQuery = query(
      notificationsRef,
      where('userId', '==', currentUser.uid),
      where('closed', '==', false),
      where('read', '==', false)
    );

    const unsubscribe = onSnapshot(notificationsQuery, (snapshot) => {
      setNotificationCount(snapshot.size);
    });

    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        if (currentUser) {
          const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            setLocalUserRole(userData.role);
          }
        }
      } catch (error) {
        console.error('Error fetching user role:', error);
      }
    };

    fetchUserRole();
  }, [currentUser]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        modalRef.current &&
        !modalRef.current.contains(event.target) &&
        !searchButtonRef.current?.contains(event.target)
      ) {
        setShowSearchModal(false);
      }
    };

    if (showSearchModal) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showSearchModal]);

  // Refresh the display name at most once every five minutes.
  useEffect(() => {
    // Refresh user data to ensure we have the latest displayName
    const updateUserData = async () => {
      try {
        const now = Date.now();
        // Only refresh if it's been more than 5 minutes since the last refresh
        // or if we haven't refreshed yet
        if (currentUser && (!hasRefreshed || now - lastRefreshTime > 300000)) {
          setHasRefreshed(true);
          setLastRefreshTime(now);
          const updatedUser = await refreshUser();
          // Only log in development environment
          if (process.env.NODE_ENV !== 'production') {
            console.log("User data refreshed in TopBar:", updatedUser?.displayName || currentUser?.displayName);
          }
        }
      } catch (error) {
        console.error("Error refreshing user data in TopBar:", error);
      }
    };
    
    updateUserData();
    // Only run this effect when currentUser changes or refreshUser function changes
  }, [currentUser?.uid, refreshUser, hasRefreshed, lastRefreshTime]);

  // Add useEffect to set a random welcome message and current date
  useEffect(() => {
    const welcomeMessages = [
      'Nice to see you again',
      'Welcome back',
      'Good to have you back',
      'Hello there',
      'Great to see you',
      'Welcome'
    ];
    
    const randomMessage = welcomeMessages[Math.floor(Math.random() * welcomeMessages.length)];
    setWelcomeMessage(randomMessage);

    // Format current date
    const now = new Date();
    const options = { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    };
    const formattedDate = now.toLocaleDateString('en-US', options);
    setCurrentDate(formattedDate);
  }, []);

  // Add useEffect to handle responsive behavior for hamburger menu and search icon
  useEffect(() => {
    const checkScreenSize = () => {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1024);
    };

    // Check on mount
    checkScreenSize();

    // Add event listener for window resize
    window.addEventListener('resize', checkScreenSize);

    // Cleanup
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const handleLogout = async () => {
    try {
      setLogoutLoading(true);
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setLogoutLoading(false);
    }
  };

  const handleNotificationsClick = () => {
    navigate('/notifications');
  };

  // Filter search results based on selected categories
  const filterResultsByCategory = useCallback((results, categories = selectedCategoriesRef.current) => {
    if (!categories || categories.length === 0) return results;
    return results.filter(result => categories.includes(result.category));
  }, []);

  const getCachedDocs = useCallback(async (cacheKey, queryOrRef) => {
    const cache = searchSnapshotCacheRef.current;
    if (cache.has(cacheKey)) return cache.get(cacheKey);
    try {
      const snap = await getDocs(queryOrRef);
      cache.set(cacheKey, snap);
      return snap;
    } catch (e) {
      console.warn(e?.message || e);
      return { docs: [] };
    }
  }, []);

  // Toggle category filter
  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  };

  // Get available categories based on user role
  const getAvailableCategories = () => {
    const baseCategories = [
      { id: 'pages', name: 'Pages', icon: FaHome },
      { id: 'properties', name: 'Properties', icon: FaBuilding },
      { id: 'maintenance', name: 'Maintenance', icon: FaTools },
      { id: 'links', name: 'Links', icon: FaChevronRight },
    ];

    if (['agent', 'owner', 'administrator'].includes(userRole)) {
      baseCategories.push(
        { id: 'tenants', name: 'Tenants', icon: FaUsers },
        { id: 'landlords', name: 'Landlords', icon: FaUser },
        { id: 'documents', name: 'Documents', icon: FaFileDownload },
        { id: 'certificates', name: 'Certificates', icon: FaCertificate },
        { id: 'calendar', name: 'Calendar', icon: FaCalendar }
      );
    }

    if (['owner', 'administrator'].includes(userRole)) {
      baseCategories.push({ id: 'users', name: 'Users', icon: FaUserTie });
      if (isFeatureEnabled(FEATURES.FINANCE_MODULE)) {
        baseCategories.push(
          { id: 'finance', name: 'Finance', icon: FaPoundSign },
          { id: 'payments', name: 'Payments', icon: FaCreditCard }
        );
      }
    }

    return baseCategories;
  };

  const accessibleSearchPages = useMemo(() => {
    const combined = [...allPages, ...settingsSections];
    return combined.filter((page) => {
      if (!isFeatureEnabled(FEATURES.FINANCE_MODULE) && (page.path === '/finance' || page.path === '/reports')) {
        return false;
      }
      if (page.path === '/messages') return false;
      if (page.adminOnly) {
        const normalizedRole = userRole?.toLowerCase();
        const isAdmin = normalizedRole === 'administrator';
        const isOwner = normalizedRole === 'owner';
        if (!isAdmin && !isOwner) return false;
      }
      if (page.pageName && userRole) {
        if (!checkPageAccess(page.pageName)) return false;
      }
      if (page.requiredFeature && userRole) {
        if (!checkFeatureAccess(page.requiredFeature)) return false;
      }
      return true;
    });
  }, [userRole, userPermissions, checkPageAccess, checkFeatureAccess]);

  // Auto suggestions for empty or short queries (pages + recent searches)
  useEffect(() => {
    if (!showSearchModal) return;
    if (searchQuery.trim().length >= 2) return;

    const suggestionGroups = buildAutoSuggestionResults({
      query: searchQuery,
      accessiblePages: accessibleSearchPages,
      recentSearches: getRecentSearches(),
    }).map((group) => ({
      ...group,
      icon: group.category === 'recent' ? FaSearch : FaHome,
    }));

    lastUnfilteredSearchRef.current = suggestionGroups;
    setSearchResults(filterResultsByCategory(suggestionGroups));
    setIsSearching(false);
  }, [searchQuery, accessibleSearchPages, showSearchModal, filterResultsByCategory]);

  // Perform universal search across multiple collections
  const performSearch = useCallback(async (searchTerm) => {
    if (!showSearchModal) return;
    if (!searchTerm || searchTerm.trim().length < 2) return;

    searchRequestIdRef.current += 1;
    const thisRequestId = searchRequestIdRef.current;
    setIsSearching(true);
    const searchResults = [];
    const lowerSearchTerm = searchTerm.toLowerCase();
    const isStale = () => thisRequestId !== searchRequestIdRef.current;

    const matchTerms = (searchTerms = []) =>
      searchTerms.some((term) => String(term).toLowerCase().includes(lowerSearchTerm));

    try {
      // Pages — in-memory
      const accessiblePages = allPages.filter((page) => {
        if (!isFeatureEnabled(FEATURES.FINANCE_MODULE) && (page.path === '/finance' || page.path === '/reports')) {
          return false;
        }
        if (page.path === '/messages') return false;
        if (page.adminOnly) {
          const normalizedRole = userRole?.toLowerCase();
          if (normalizedRole !== 'administrator' && normalizedRole !== 'owner') return false;
        }
        if (page.pageName && userRole && !checkPageAccess(page.pageName)) return false;
        if (page.requiredFeature && userRole && !checkFeatureAccess(page.requiredFeature)) return false;
        return true;
      });

      const pageMatches = accessiblePages
        .filter(
          (page) =>
            page.title?.toLowerCase().includes(lowerSearchTerm) ||
            page.path?.toLowerCase().includes(lowerSearchTerm) ||
            page.pageName?.toLowerCase().includes(lowerSearchTerm)
        )
        .slice(0, 5);

      if (pageMatches.length > 0) {
        searchResults.push({
          category: 'pages',
          icon: FaHome,
          items: pageMatches.map((page) => ({
            id: page.path,
            title: page.title,
            path: page.path,
          })),
        });
      }

      if (userRole && checkPageAccess('settings')) {
        const settingsMatches = settingsSections
          .filter((section) => {
            if (section.requiredFeature && userRole && !hasFeatureAccessSync(userRole, section.requiredFeature)) {
              return false;
            }
            return (
              section.title?.toLowerCase().includes(lowerSearchTerm) ||
              section.path?.toLowerCase().includes(lowerSearchTerm) ||
              section.pageName?.toLowerCase().includes(lowerSearchTerm)
            );
          })
          .slice(0, 5);

        if (settingsMatches.length > 0) {
          searchResults.push({
            category: 'pages',
            icon: FaCog,
            items: settingsMatches.map((section) => ({
              id: section.path,
              title: section.title,
              subtitle: 'Settings section',
              path: section.path,
            })),
          });
        }
      }

      // Wave 1: properties + links (+ agent properties when needed)
      const propertiesRef = collection(db, 'properties');
      let propertiesQuery = propertiesRef;
      let propertiesCacheKey = 'properties:all';
      if (userRole === 'landlord' && currentUser) {
        propertiesQuery = query(propertiesRef, where('landlordId', '==', currentUser.uid));
        propertiesCacheKey = `properties:landlord:${currentUser.uid}`;
      }

      const linksQuery = query(collection(db, 'links'), limit(SEARCH_SCAN_LIMIT));
      const wave1 = [
        getCachedDocs(propertiesCacheKey, propertiesQuery),
        getCachedDocs('links', linksQuery),
      ];

      const needAgentProps = userRole === 'agent' && currentUser;
      if (needAgentProps) {
        wave1.push(
          getCachedDocs(
            `properties:agent:${currentUser.uid}`,
            query(propertiesRef, where('agentId', '==', currentUser.uid))
          )
        );
      }

      const [propertiesSnapshot, linksSnapshot, agentPropertiesSnapshot] = await Promise.all(wave1);
      if (isStale()) return;

      const propertyMatches = propertiesSnapshot.docs
        .filter((docSnap) => {
          const data = docSnap.data();
          return (
            data.address?.toLowerCase().includes(lowerSearchTerm) ||
            data.propertyType?.toLowerCase().includes(lowerSearchTerm) ||
            data.propertyName?.toLowerCase().includes(lowerSearchTerm) ||
            matchTerms(data.searchTerms)
          );
        })
        .slice(0, 5);

      if (propertyMatches.length > 0) {
        searchResults.push({
          category: 'properties',
          icon: FaBuilding,
          items: propertyMatches.map((docSnap) => {
            const data = docSnap.data();
            return {
              id: docSnap.id,
              title: data.address || data.propertyName || 'Unnamed Property',
              subtitle: `${data.bedrooms || 'N/A'} bed ${data.propertyType || 'Property'}`,
            };
          }),
        });
      }

      const propertyAddressById = new Map(
        propertiesSnapshot.docs.map((d) => {
          const data = d.data();
          return [d.id, data.address || data.propertyName || null];
        })
      );

      const landlordPropertyIds =
        userRole === 'landlord' ? propertiesSnapshot.docs.map((d) => d.id) : [];
      const agentPropertyIds = needAgentProps
        ? (agentPropertiesSnapshot?.docs || []).map((d) => d.id)
        : [];

      // Wave 2: independent collections in parallel
      const wave2 = {};
      const wave2Promises = [];

      const enqueue = (key, promise) => {
        wave2Promises.push(
          promise.then((snap) => {
            wave2[key] = snap;
          })
        );
      };

      if (['agent', 'owner', 'administrator'].includes(userRole)) {
        const tenantsRef = collection(db, 'tenants');
        const tenantsQuery =
          userRole === 'agent' && currentUser
            ? query(tenantsRef, where('agentId', '==', currentUser.uid))
            : query(tenantsRef, limit(SEARCH_SCAN_LIMIT));
        const tenantsKey =
          userRole === 'agent' && currentUser
            ? `tenants:agent:${currentUser.uid}`
            : 'tenants:capped';
        enqueue(tenantsKey, getCachedDocs(tenantsKey, tenantsQuery));
        wave2._tenantsKey = tenantsKey;

        enqueue(
          'landlords',
          getCachedDocs('landlords', query(collection(db, 'users'), where('role', '==', 'landlord')))
        );
      }

      const maintenanceRef = collection(db, 'maintenanceRequests');
      if (userRole === 'landlord' && landlordPropertyIds.length > 0) {
        // batched below after wave2 for property-scoped roles
      } else if (userRole === 'agent' && agentPropertyIds.length > 0) {
        // batched below
      } else if (['owner', 'administrator'].includes(userRole)) {
        enqueue(
          'maintenance',
          getCachedDocs('maintenance:capped', query(maintenanceRef, limit(SEARCH_SCAN_LIMIT)))
        );
      }

      if (isFeatureEnabled(FEATURES.FINANCE_MODULE) && ['owner', 'administrator'].includes(userRole)) {
        const financeRef = collection(db, 'finances');
        const financeQuery =
          userRole === 'owner' && currentUser
            ? query(financeRef, where('ownerId', '==', currentUser.uid))
            : query(financeRef, limit(SEARCH_SCAN_LIMIT));
        const financeKey =
          userRole === 'owner' && currentUser
            ? `finances:owner:${currentUser.uid}`
            : 'finances:capped';
        enqueue(financeKey, getCachedDocs(financeKey, financeQuery));
        wave2._financeKey = financeKey;
      }

      enqueue('documents', getCachedDocs('documents:capped', query(collection(db, 'documents'), limit(SEARCH_SCAN_LIMIT))));

      if (['owner', 'administrator'].includes(userRole)) {
        enqueue('users', getCachedDocs('users:capped', query(collection(db, 'users'), limit(SEARCH_SCAN_LIMIT))));
      }

      if (isFeatureEnabled(FEATURES.FINANCE_MODULE) && ['owner', 'administrator'].includes(userRole)) {
        enqueue('payments', getCachedDocs('payments:capped', query(collection(db, 'payments'), limit(SEARCH_SCAN_LIMIT))));
        if (userRole === 'owner' && currentUser) {
          enqueue(
            `properties:landlord:${currentUser.uid}`,
            getCachedDocs(
              `properties:landlord:${currentUser.uid}`,
              query(propertiesRef, where('landlordId', '==', currentUser.uid))
            )
          );
          wave2._ownerPropsKey = `properties:landlord:${currentUser.uid}`;
        }
      }

      if (['agent', 'owner', 'administrator', 'landlord'].includes(userRole)) {
        enqueue(
          'certificates',
          getCachedDocs('certificates:capped', query(collection(db, 'certificates'), limit(SEARCH_SCAN_LIMIT)))
        );
        enqueue(
          'events',
          getCachedDocs('events:capped', query(collection(db, 'events'), limit(SEARCH_SCAN_LIMIT)))
        );
      }

      await Promise.all(wave2Promises);
      if (isStale()) return;

      // Maintenance batches for landlord/agent (reuse property ids — no re-query)
      let maintenanceDocs = wave2.maintenance?.docs || [];
      const scopedPropertyIds =
        userRole === 'landlord' ? landlordPropertyIds : userRole === 'agent' ? agentPropertyIds : [];

      if ((userRole === 'landlord' || userRole === 'agent') && scopedPropertyIds.length > 0) {
        const cacheKey = `maintenance:props:${scopedPropertyIds.slice().sort().join(',')}`;
        if (searchSnapshotCacheRef.current.has(cacheKey)) {
          maintenanceDocs = searchSnapshotCacheRef.current.get(cacheKey).docs;
        } else {
          const batchSize = 10;
          const allDocs = [];
          for (let i = 0; i < scopedPropertyIds.length; i += batchSize) {
            if (isStale()) return;
            const batchIds = scopedPropertyIds.slice(i, i + batchSize);
            const batchSnap = await getCachedDocs(
              `maintenance:batch:${batchIds.join(',')}`,
              query(maintenanceRef, where('propertyId', 'in', batchIds))
            );
            allDocs.push(...batchSnap.docs);
          }
          const combined = { docs: allDocs };
          searchSnapshotCacheRef.current.set(cacheKey, combined);
          maintenanceDocs = allDocs;
        }
      }

      if (isStale()) return;

      // Tenants
      if (['agent', 'owner', 'administrator'].includes(userRole)) {
        const tenantsSnapshot = wave2[wave2._tenantsKey] || { docs: [] };
        const tenantMatches = tenantsSnapshot.docs
          .filter((docSnap) => {
            const data = docSnap.data();
            return (
              data.firstName?.toLowerCase().includes(lowerSearchTerm) ||
              data.lastName?.toLowerCase().includes(lowerSearchTerm) ||
              data.email?.toLowerCase().includes(lowerSearchTerm) ||
              `${data.firstName || ''} ${data.lastName || ''}`.toLowerCase().includes(lowerSearchTerm) ||
              matchTerms(data.searchTerms)
            );
          })
          .slice(0, 5);

        if (tenantMatches.length > 0) {
          searchResults.push({
            category: 'tenants',
            icon: FaUsers,
            items: tenantMatches.map((docSnap) => {
              const data = docSnap.data();
              const address =
                data.propertyAddress ||
                (data.propertyId ? propertyAddressById.get(data.propertyId) : null);
              return {
                id: docSnap.id,
                title: `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Unnamed Tenant',
                subtitle: address || 'No property assigned',
              };
            }),
          });
        }
      }

      // Maintenance filter
      if (maintenanceDocs.length > 0 || ['owner', 'administrator', 'landlord', 'agent'].includes(userRole)) {
        const maintenanceMatches = maintenanceDocs
          .filter((docSnap) => {
            const data = docSnap.data();
            return (
              data.title?.toLowerCase().includes(lowerSearchTerm) ||
              data.description?.toLowerCase().includes(lowerSearchTerm) ||
              data.status?.toLowerCase().includes(lowerSearchTerm) ||
              data.propertyAddress?.toLowerCase().includes(lowerSearchTerm) ||
              matchTerms(data.searchTerms)
            );
          })
          .slice(0, 5);

        if (maintenanceMatches.length > 0) {
          searchResults.push({
            category: 'maintenance',
            icon: FaTools,
            items: maintenanceMatches.map((docSnap) => {
              const data = docSnap.data();
              return {
                id: docSnap.id,
                title: data.title || 'Untitled Request',
                subtitle: `${data.status || 'No status'} - ${data.propertyAddress || 'Unknown Property'}`,
              };
            }),
          });
        }
      }

      // Landlords
      if (['agent', 'owner', 'administrator'].includes(userRole)) {
        const landlordsSnapshot = wave2.landlords || { docs: [] };
        const landlordMatches = landlordsSnapshot.docs
          .filter((docSnap) => {
            const data = docSnap.data();
            const fullName = `${data.firstName || ''} ${data.lastName || ''}`.trim().toLowerCase();
            return (
              data.firstName?.toLowerCase().includes(lowerSearchTerm) ||
              data.lastName?.toLowerCase().includes(lowerSearchTerm) ||
              data.email?.toLowerCase().includes(lowerSearchTerm) ||
              fullName.includes(lowerSearchTerm) ||
              data.phone?.toLowerCase().includes(lowerSearchTerm) ||
              data.company?.toLowerCase().includes(lowerSearchTerm) ||
              matchTerms(data.searchTerms)
            );
          })
          .slice(0, 5);

        if (landlordMatches.length > 0) {
          searchResults.push({
            category: 'landlords',
            icon: FaUserTie,
            items: landlordMatches.map((docSnap) => {
              const data = docSnap.data();
              return {
                id: docSnap.id,
                title: `${data.firstName || ''} ${data.lastName || ''}`.trim() || 'Unnamed Landlord',
                subtitle: data.email || data.phone || 'No contact info',
              };
            }),
          });
        }
      }

      // Finance
      if (isFeatureEnabled(FEATURES.FINANCE_MODULE) && ['owner', 'administrator'].includes(userRole)) {
        const financeSnapshot = wave2[wave2._financeKey] || { docs: [] };
        const financeMatches = financeSnapshot.docs
          .filter((docSnap) => {
            const data = docSnap.data();
            return (
              data.type?.toLowerCase().includes(lowerSearchTerm) ||
              data.propertyAddress?.toLowerCase().includes(lowerSearchTerm) ||
              data.description?.toLowerCase().includes(lowerSearchTerm) ||
              matchTerms(data.searchTerms)
            );
          })
          .slice(0, 5);

        if (financeMatches.length > 0) {
          searchResults.push({
            category: 'finance',
            icon: FaPoundSign,
            items: financeMatches.map((docSnap) => {
              const data = docSnap.data();
              return {
                id: docSnap.id,
                title: `${data.type} - £${data.amount}`,
                subtitle: `${data.propertyAddress} - ${new Date(data.date).toLocaleDateString()}`,
              };
            }),
          });
        }
      }

      // Links
      const linkMatches = linksSnapshot.docs
        .filter((docSnap) => {
          const data = docSnap.data();
          return (
            data.title?.toLowerCase().includes(lowerSearchTerm) ||
            data.description?.toLowerCase().includes(lowerSearchTerm) ||
            data.category?.toLowerCase().includes(lowerSearchTerm) ||
            data.url?.toLowerCase().includes(lowerSearchTerm) ||
            matchTerms(data.searchTerms)
          );
        })
        .slice(0, 5);

      if (linkMatches.length > 0) {
        searchResults.push({
          category: 'links',
          icon: FaChevronRight,
          items: linkMatches.map((docSnap) => {
            const data = docSnap.data();
            return {
              id: docSnap.id,
              title: data.title || 'Untitled Link',
              subtitle: data.category || data.description || 'No description',
            };
          }),
        });
      }

      // Documents — reuse landlordPropertyIds (no re-query properties)
      {
        const documentsSnapshot = wave2.documents || { docs: [] };
        const documentMatches = documentsSnapshot.docs
          .filter((docSnap) => {
            const data = docSnap.data();
            if (userRole === 'landlord') {
              if (!landlordPropertyIds.includes(data.propertyId)) return false;
            }
            return (
              data.name?.toLowerCase().includes(lowerSearchTerm) ||
              data.type?.toLowerCase().includes(lowerSearchTerm) ||
              data.category?.toLowerCase().includes(lowerSearchTerm) ||
              data.description?.toLowerCase().includes(lowerSearchTerm) ||
              data.fileName?.toLowerCase().includes(lowerSearchTerm) ||
              matchTerms(data.searchTerms)
            );
          })
          .slice(0, 5);

        if (documentMatches.length > 0) {
          searchResults.push({
            category: 'documents',
            icon: FaFileDownload,
            items: documentMatches.map((docSnap) => {
              const data = docSnap.data();
              const address =
                data.propertyAddress ||
                (data.propertyId ? propertyAddressById.get(data.propertyId) : null);
              return {
                id: docSnap.id,
                propertyId: data.propertyId,
                title: data.name || data.fileName || 'Untitled Document',
                subtitle: `${data.type || 'Document'} - ${address || 'Unknown Property'}`,
              };
            }),
          });
        }
      }

      // Users (non-landlord)
      if (['owner', 'administrator'].includes(userRole)) {
        const usersSnapshot = wave2.users || { docs: [] };
        const userMatches = usersSnapshot.docs
          .filter((docSnap) => {
            const data = docSnap.data();
            if (data.role === 'landlord') return false;
            const fullName = `${data.firstName || ''} ${data.lastName || ''}`.trim().toLowerCase();
            return (
              data.firstName?.toLowerCase().includes(lowerSearchTerm) ||
              data.lastName?.toLowerCase().includes(lowerSearchTerm) ||
              data.email?.toLowerCase().includes(lowerSearchTerm) ||
              fullName.includes(lowerSearchTerm) ||
              data.phone?.toLowerCase().includes(lowerSearchTerm) ||
              data.role?.toLowerCase().includes(lowerSearchTerm) ||
              data.company?.toLowerCase().includes(lowerSearchTerm) ||
              matchTerms(data.searchTerms)
            );
          })
          .slice(0, 5);

        if (userMatches.length > 0) {
          searchResults.push({
            category: 'users',
            icon: FaUserTie,
            items: userMatches.map((docSnap) => {
              const data = docSnap.data();
              return {
                id: docSnap.id,
                title: `${data.firstName || ''} ${data.lastName || ''}`.trim() || data.email || 'Unnamed User',
                subtitle: `${data.role || 'No role'} - ${data.email || 'No email'}`,
              };
            }),
          });
        }
      }

      // Payments — reuse property ids for owner filter
      if (isFeatureEnabled(FEATURES.FINANCE_MODULE) && ['owner', 'administrator'].includes(userRole)) {
        const paymentsSnapshot = wave2.payments || { docs: [] };
        const ownerPropertyIds =
          userRole === 'owner' ? propertiesSnapshot.docs.map((d) => d.id) : null;

        const paymentMatches = paymentsSnapshot.docs
          .filter((docSnap) => {
            const data = docSnap.data();
            if (ownerPropertyIds && !ownerPropertyIds.includes(data.propertyId)) return false;
            return (
              data.tenantName?.toLowerCase().includes(lowerSearchTerm) ||
              data.propertyAddress?.toLowerCase().includes(lowerSearchTerm) ||
              data.type?.toLowerCase().includes(lowerSearchTerm) ||
              data.status?.toLowerCase().includes(lowerSearchTerm) ||
              data.amount?.toString().includes(lowerSearchTerm) ||
              matchTerms(data.searchTerms)
            );
          })
          .slice(0, 5);

        if (paymentMatches.length > 0) {
          searchResults.push({
            category: 'payments',
            icon: FaCreditCard,
            items: paymentMatches.map((docSnap) => {
              const data = docSnap.data();
              const dueDate = data.dueDate?.toDate
                ? data.dueDate.toDate()
                : data.dueDate
                  ? new Date(data.dueDate)
                  : null;
              return {
                id: docSnap.id,
                title: `£${data.amount || '0'} - ${data.type || 'Payment'}`,
                subtitle: `${data.propertyAddress || 'Unknown Property'} - ${dueDate ? dueDate.toLocaleDateString() : 'No date'}`,
              };
            }),
          });
        }
      }

      // Certificates — reuse landlordPropertyIds
      if (['agent', 'owner', 'administrator', 'landlord'].includes(userRole)) {
        const certificatesSnapshot = wave2.certificates || { docs: [] };
        const certificateMatches = certificatesSnapshot.docs
          .filter((docSnap) => {
            const data = docSnap.data();
            if (userRole === 'landlord' && !landlordPropertyIds.includes(data.propertyId)) return false;
            return (
              data.type?.toLowerCase().includes(lowerSearchTerm) ||
              data.propertyAddress?.toLowerCase().includes(lowerSearchTerm) ||
              data.status?.toLowerCase().includes(lowerSearchTerm) ||
              data.certificateNumber?.toLowerCase().includes(lowerSearchTerm) ||
              matchTerms(data.searchTerms)
            );
          })
          .slice(0, 5);

        if (certificateMatches.length > 0) {
          searchResults.push({
            category: 'certificates',
            icon: FaCertificate,
            items: certificateMatches.map((docSnap) => {
              const data = docSnap.data();
              const expiryDate = data.expiryDate?.toDate
                ? data.expiryDate.toDate()
                : data.expiryDate
                  ? new Date(data.expiryDate)
                  : null;
              return {
                id: docSnap.id,
                title: `${data.type || 'Certificate'} - ${data.propertyAddress || 'Unknown Property'}`,
                subtitle: `Expires: ${expiryDate ? expiryDate.toLocaleDateString() : 'No expiry date'}`,
              };
            }),
          });
        }
      }

      // Calendar events — reuse landlordPropertyIds
      if (['agent', 'owner', 'administrator', 'landlord'].includes(userRole)) {
        const eventsSnapshot = wave2.events || { docs: [] };
        const eventMatches = eventsSnapshot.docs
          .filter((docSnap) => {
            const data = docSnap.data();
            if (
              userRole === 'landlord' &&
              data.propertyId &&
              !landlordPropertyIds.includes(data.propertyId)
            ) {
              return false;
            }
            return (
              data.title?.toLowerCase().includes(lowerSearchTerm) ||
              data.description?.toLowerCase().includes(lowerSearchTerm) ||
              data.type?.toLowerCase().includes(lowerSearchTerm) ||
              data.location?.toLowerCase().includes(lowerSearchTerm) ||
              matchTerms(data.searchTerms)
            );
          })
          .slice(0, 5);

        if (eventMatches.length > 0) {
          searchResults.push({
            category: 'calendar',
            icon: FaCalendar,
            items: eventMatches.map((docSnap) => {
              const data = docSnap.data();
              const eventDate = data.startDate?.toDate
                ? data.startDate.toDate()
                : data.startDate
                  ? new Date(data.startDate)
                  : null;
              return {
                id: docSnap.id,
                title: data.title || 'Untitled Event',
                subtitle: `${eventDate ? eventDate.toLocaleDateString() : 'No date'} - ${data.type || 'Event'}`,
              };
            }),
          });
        }
      }

      if (isStale()) return;
      lastUnfilteredSearchRef.current = searchResults;
      setSearchResults(filterResultsByCategory(searchResults));
    } catch (error) {
      if (isStale()) return;
      console.error('Search error:', error);
      setSearchResults([
        {
          category: 'error',
          items: [
            {
              id: 'error',
              title: 'Error performing search',
              subtitle: 'Please try again later',
            },
          ],
        },
      ]);
    } finally {
      if (!isStale()) setIsSearching(false);
    }
  }, [
    showSearchModal,
    userRole,
    currentUser,
    getCachedDocs,
    filterResultsByCategory,
    checkPageAccess,
    checkFeatureAccess,
  ]);

  // Clear session snapshot cache when search modal closes
  useEffect(() => {
    if (showSearchModal) return;
    searchSnapshotCacheRef.current.clear();
    searchRequestIdRef.current += 1;
  }, [showSearchModal]);

  // Single debounced search — only while modal is open
  useEffect(() => {
    if (!showSearchModal) return;
    if (!searchQuery || searchQuery.trim().length < 2) return;

    const timer = setTimeout(() => {
      performSearch(searchQuery);
    }, 250);

    return () => clearTimeout(timer);
  }, [searchQuery, showSearchModal, performSearch]);

  // Category filter: refilter in memory only (no re-fetch)
  useEffect(() => {
    if (!showSearchModal) return;
    setSearchResults(filterResultsByCategory(lastUnfilteredSearchRef.current));
  }, [selectedCategories, showSearchModal, filterResultsByCategory]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Command/Ctrl + K to open search (focus handled by useEffect when modal opens)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setShowSearchModal(true);
      }
      // Escape to close search modal
      if (e.key === 'Escape') {
        setShowSearchModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Auto-focus search input when modal opens so user can type immediately
  useEffect(() => {
    if (!showSearchModal) return;
    const timeoutId = setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timeoutId);
  }, [showSearchModal]);

  // Handle search input change
  const handleSearchInputChange = useCallback((event) => {
    setSearchQuery(event.target.value);
  }, []);

  const handleSearchResultSelect = useCallback(
    (item) => {
      if (!item) return;
      if (item.category === 'recent') {
        const q = item.query || item.title || '';
        setSearchQuery(q);
        return;
      }
      if (searchQuery.trim().length >= 2) {
        addRecentSearch(searchQuery.trim());
      }
      setShowSearchModal(false);
      if (item.category === 'properties') {
        navigate(`/properties/${item.id}`);
      } else if (item.category === 'tenants') {
        navigate(`/tenants/${item.id}`);
      } else if (item.category === 'maintenance') {
        navigate(`/maintenance/${item.id}`);
      } else if (item.category === 'finance') {
        navigate('/finance');
      } else if (item.category === 'payments') {
        navigate(`/edit-payment/${item.id}`);
      } else if (item.category === 'landlords') {
        navigate(`/landlords/${item.id}`);
      } else if (item.category === 'users') {
        navigate(`/settings?tab=users&userId=${item.id}`);
      } else if (item.category === 'links') {
        navigate('/links');
      } else if (item.category === 'documents') {
        navigate(item.propertyId ? `/properties/${item.propertyId}?tab=documents` : '/properties');
      } else if (item.category === 'certificates') {
        navigate('/compliance');
      } else if (item.category === 'calendar') {
        navigate('/calendar');
      } else if (item.category === 'messages') {
        navigate(`/messages?conversation=${item.id}`);
      } else if (item.category === 'pages' && item.path) {
        navigate(item.path);
      }
    },
    [navigate, searchQuery]
  );

  useEffect(() => {
    setMobileNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMobileNavOpen(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [mobileNavOpen]);

  const t = todayTokens(isDarkMode);
  const todayVars = {
    fontFamily: SANS,
    '--t-bg': t.bg, '--t-surface': t.surface, '--t-ink': t.ink, '--t-muted': t.muted,
    '--t-line': t.line, '--t-navy': t.navy, '--t-rust': t.rust, '--t-accent': t.accent, '--t-panel': t.panel,
  };
  const itemByPath = new Map(filteredNavItems.map((item) => [item.path, item]));
  const sidebarGroups = SIDEBAR_GROUP_CONFIG
    .map((g) => ({ title: g.title, items: g.paths.map((p) => itemByPath.get(p)).filter(Boolean) }))
    .filter((g) => g.items.length > 0);
  const role = (userRole || '').toLowerCase();
  const userName = currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Account';

  const searchModal = (
    <GlobalSearchModal
      open={showSearchModal}
      onClose={() => setShowSearchModal(false)}
      modalRef={modalRef}
      isDarkMode={isDarkMode}
      primaryColor={primaryColor}
      searchQuery={searchQuery}
      onSearchInputChange={handleSearchInputChange}
      searchInputRef={searchInputRef}
      showFilters={showFilters}
      onToggleFilters={() => setShowFilters((prev) => !prev)}
      selectedCategories={selectedCategories}
      onToggleCategory={toggleCategory}
      onClearCategories={() => setSelectedCategories([])}
      availableCategories={getAvailableCategories()}
      searchResults={searchResults}
      isSearching={isSearching}
      onSelectResult={handleSearchResultSelect}
    />
  );

  if (!isMobile && !isTablet) {
    const sidebarLogo = getDisplayLogo(true);
    const DEFAULT_CREATE = [
      { label: 'Property', path: '/properties/add', page: 'properties' },
      { label: 'Tenant', path: '/tenants/add', page: 'tenants' },
      { label: 'Landlord', path: '/add-landlord', page: 'landlords' },
      { label: 'Enquiry', path: '/enquiries/add', page: 'enquiries' },
      { label: 'Log a repair', path: '/maintenance?new=repair', page: 'maintenance' },
      { label: 'Book viewing', path: '/bookings?new=viewing', page: 'enquiries' },
    ];
    // Per-page Create actions: a single item renders as a direct button, several as the Create menu.
    // Dialog-based pages open via ?new=<key>, handled by useNewParam on that page.
    const PAGE_CREATE = {
      '/properties': [{ label: 'Add property', path: '/properties/add', page: 'properties' }],
      '/sales': [
        { label: 'Add listing', path: '/sales?new=listing', page: 'sales' },
        { label: 'Vendor enquiry', path: '/enquiries/add?type=vendor', page: 'enquiries' },
        { label: 'Buyer enquiry', path: '/enquiries/add?type=buyer', page: 'enquiries' },
      ],
      '/tenants': [{ label: 'Add tenant', path: '/tenants/add', page: 'tenants' }],
      '/landlords': [
        { label: 'Add landlord', path: '/add-landlord', page: 'landlords' },
        { label: 'Hold landlord', path: '/landlords?new=hold', page: 'landlords' },
      ],
      '/contractors': [{ label: 'Add contractor', path: '/contractors?new=contractor', page: 'contractors' }],
      '/contacts': [
        { label: 'Add contact', path: '/contacts?new=contact', page: 'properties' },
        { label: 'Add group', path: '/contacts?new=group', page: 'properties' },
      ],
      '/enquiries': [
        { label: 'Tenant enquiry', path: '/enquiries/add?type=tenant', page: 'enquiries' },
        { label: 'Landlord enquiry', path: '/enquiries/add?type=landlord', page: 'enquiries' },
        { label: 'Vendor enquiry', path: '/enquiries/add?type=vendor', page: 'enquiries' },
        { label: 'Buyer enquiry', path: '/enquiries/add?type=buyer', page: 'enquiries' },
      ],
      '/bookings': [{ label: 'Book viewing', path: '/bookings?new=viewing', page: 'enquiries' }],
      '/maintenance': [{ label: 'Log a repair', path: '/maintenance?new=repair', page: 'maintenance' }],
      '/compliance': [
        { label: 'Upload certificate', path: '/compliance?new=certificate', page: 'properties' },
        { label: 'Bulk fetch certificates', path: '/compliance?new=bulk-fetch', page: 'properties' },
      ],
      '/inspections': [
        { label: 'Book inspection', path: '/inspections?new=inspection', page: 'inspections' },
        { label: 'Add event', path: '/inspections?new=event', page: 'inspections' },
        { label: 'Inspection report builder', path: '/inspection-report-builder', page: 'inspectionReportBuilder', feature: FEATURES.INSPECTION_REPORT_BUILDER },
      ],
      '/inspection-report-builder': [
        { label: 'Saved reports', path: '/inspection-report-builder?new=saved', page: 'inspectionReportBuilder' },
        { label: 'New report', path: '/inspection-report-builder?new=report', page: 'inspectionReportBuilder' },
      ],
      '/finance': [{ label: 'Add payment', path: '/finance?new=payment', page: 'finance' }],
      '/broadcast': [{ label: 'Compose broadcast', path: '/broadcast?new=broadcast', page: 'broadcast' }],
      '/links': [{ label: 'Add link', path: '/links?new=link', page: 'links' }],
      '/company-documents': [{ label: 'Add document', path: '/company-documents?new=document', page: 'companyDocuments' }],
    };
    const pageCreate = PAGE_CREATE[location.pathname];
    const createItems = role === 'landlord' || role === 'tenant' ? [] : (pageCreate || DEFAULT_CREATE)
      .filter((c) => checkPageAccess(c.page) && (!c.feature || isFeatureEnabled(c.feature)));
    const pageAction = pageCreate && createItems.length === 1 ? createItems[0] : null;

    return (
      <div className="today-shell" style={todayVars}>
        <nav className="today-sidebar" aria-label="Main">
          <PageHandler path="/dashboard" className="today-sidebar__brand" aria-label="Go to dashboard">
            {sidebarLogo ? (
              <img src={sidebarLogo} alt="Company logo" className="today-sidebar__logo" />
            ) : (
              <>
                <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true">
                  <rect x="2" y="3" width="7" height="24" fill="#F2EFE7" />
                  <rect x="11.5" y="3" width="7" height="24" fill="#F2EFE7" />
                  <rect x="21" y="3" width="7" height="24" fill={t.accent} />
                </svg>
                <span style={{ fontFamily: SERIF, fontSize: 26, lineHeight: 1 }}>Plutoo</span>
              </>
            )}
          </PageHandler>

          {sidebarGroups.map((g) => (
            <div key={g.title} className="today-sidebar__group">
              <div className="today-sidebar__group-title">{g.title}</div>
              {g.items.map((item) => {
                const active = isNavItemActive(item.path, location.pathname);
                const ItemIcon = item.icon;
                return (
                  <PageHandler
                    key={item.path}
                    path={item.path}
                    aria-label={item.title}
                    aria-current={active ? 'page' : undefined}
                    className={`today-sidebar__link${active ? ' today-sidebar__link--active' : ''}`}
                  >
                    <span className="today-sidebar__icon" aria-hidden="true">{ItemIcon && <ItemIcon size={16} />}</span>
                    <span>{item.shortTitle || item.title}</span>
                  </PageHandler>
                );
              })}
            </div>
          ))}

          <Menu as="div" className="today-sidebar__user">
            <Menu.Button className="today-sidebar__user-btn">
              {currentUser?.photoURL ? (
                <img src={currentUser.photoURL} alt="" className="today-sidebar__avatar" />
              ) : (
                <span className="today-sidebar__avatar" aria-hidden="true">{getUserInitial()}</span>
              )}
              <span className="today-sidebar__user-text">
                <span className="today-sidebar__user-name">{userName}</span>
                {userRole && <span className="today-sidebar__user-role">{userRole}</span>}
              </span>
              <FaChevronDown size={10} aria-hidden="true" />
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <AvatarUserMenu
                currentUser={currentUser}
                userRole={userRole}
                isDarkMode={isDarkMode}
                logoutLoading={logoutLoading}
                onNavigate={navigate}
                onLogout={handleLogout}
              />
            </Transition>
          </Menu>
        </nav>

        <div className="today-shell__main">
          <header className="today-header">
            {React.cloneElement(searchModal, { anchored: true, onOpen: () => setShowSearchModal(true) })}
            <div style={{ flexGrow: 1 }} />
            {role === 'administrator' && (
              <button
                type="button"
                className="today-header__icon-btn"
                onClick={toggleTheme}
                aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
              >
                {isDarkMode ? <FaMoon size={17} /> : <FaSun size={17} />}
              </button>
            )}
            {currentUser && checkPageAccess('notifications') && (
              <button
                type="button"
                className="today-header__icon-btn"
                onClick={handleNotificationsClick}
                aria-label={notificationCount > 0 ? `Notifications, ${notificationCount} unread` : 'Notifications'}
              >
                <FaBell size={18} aria-hidden="true" />
                {notificationCount > 0 && (
                  <span className="today-header__badge">{notificationCount > 99 ? '99+' : notificationCount}</span>
                )}
              </button>
            )}
            {pageAction && (
              <button type="button" className="today-header__create" onClick={() => navigate(pageAction.path)}>
                {pageAction.label}
              </button>
            )}
            {!pageAction && createItems.length > 0 && (
              <Menu as="div" className="relative">
                <Menu.Button className="today-header__create">
                  Create
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="today-header__create-menu">
                    {createItems.map((c) => (
                      <Menu.Item key={c.path}>
                        {({ active }) => (
                          <button
                            type="button"
                            className={`today-header__create-item${active ? ' today-header__create-item--active' : ''}`}
                            onClick={() => navigate(c.path)}
                          >
                            {c.label}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
              </Menu>
            )}
          </header>
          {children}
        </div>
      </div>
    );
  }

  const headerLogo = getDisplayLogo(isDarkMode);

  return (
    <div className="today-shell today-shell--mobile" style={todayVars}>
      <header className="today-header today-header--mobile">
        <button
          type="button"
          className="today-header__brand"
          onClick={() => navigate('/dashboard')}
          aria-label="Go to dashboard"
        >
          {headerLogo ? (
            <img src={headerLogo} alt="Company logo" className="today-header__logo" />
          ) : (
            <svg width="30" height="30" viewBox="0 0 30 30" aria-hidden="true">
              <rect x="2" y="3" width="7" height="24" fill={t.ink} />
              <rect x="11.5" y="3" width="7" height="24" fill={t.ink} />
              <rect x="21" y="3" width="7" height="24" fill={t.accent} />
            </svg>
          )}
        </button>
        <div className="today-header__actions">
          <button
            type="button"
            className="today-header__icon-btn"
            onClick={() => setShowSearchModal(true)}
            ref={searchButtonRef}
            aria-label="Open search"
          >
            <FaSearch size={16} />
          </button>
          {currentUser && checkPageAccess('notifications') && (
            <button
              type="button"
              className="today-header__icon-btn"
              onClick={handleNotificationsClick}
              aria-label={notificationCount > 0 ? `Notifications, ${notificationCount} unread` : 'Notifications'}
            >
              <FaBell size={17} aria-hidden="true" />
              {notificationCount > 0 && (
                <span className="today-header__badge">{notificationCount > 99 ? '99+' : notificationCount}</span>
              )}
            </button>
          )}
          {role === 'administrator' && (
            <button
              type="button"
              className="today-header__icon-btn"
              onClick={toggleTheme}
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {isDarkMode ? <FaMoon size={16} /> : <FaSun size={16} />}
            </button>
          )}
          <Menu as="div" className="relative">
            <Menu.Button className="today-header__icon-btn today-header__avatar" aria-label="Account menu">
              {currentUser?.photoURL ? (
                <img src={currentUser.photoURL} alt="" className="today-header__avatar-img" />
              ) : (
                getUserInitial()
              )}
            </Menu.Button>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <AvatarUserMenu
                currentUser={currentUser}
                userRole={userRole}
                isDarkMode={isDarkMode}
                logoutLoading={logoutLoading}
                onNavigate={navigate}
                onLogout={handleLogout}
              />
            </Transition>
          </Menu>
          <button
            type="button"
            className="today-header__icon-btn"
            onClick={() => setMobileNavOpen(true)}
            aria-label="Open navigation menu"
          >
            <FaBars size={16} />
          </button>
        </div>
      </header>

      <AnimatePresence>
        {mobileNavOpen && (
          <>
            <motion.button
              type="button"
              className="today-mobile-nav__backdrop"
              aria-label="Close navigation menu"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setMobileNavOpen(false)}
            />
            <motion.nav
              className="today-sidebar today-sidebar--drawer"
              aria-label="Mobile navigation"
              initial={{ x: 24, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 24, opacity: 0 }}
              transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="today-mobile-nav__head">
                <span className="today-sidebar__group-title" style={{ paddingBottom: 0 }}>Menu</span>
                <button
                  type="button"
                  className="today-header__icon-btn today-header__icon-btn--on-panel"
                  onClick={() => setMobileNavOpen(false)}
                  aria-label="Close navigation menu"
                >
                  <FaTimes size={14} />
                </button>
              </div>
              {sidebarGroups.map((g) => (
                <div key={g.title} className="today-sidebar__group">
                  <div className="today-sidebar__group-title">{g.title}</div>
                  {g.items.map((item) => {
                    const active = isNavItemActive(item.path, location.pathname);
                    const ItemIcon = item.icon;
                    return (
                      <PageHandler
                        key={item.path}
                        path={item.path}
                        aria-label={item.title}
                        aria-current={active ? 'page' : undefined}
                        className={`today-sidebar__link${active ? ' today-sidebar__link--active' : ''}`}
                        onClick={() => setMobileNavOpen(false)}
                      >
                        <span className="today-sidebar__icon" aria-hidden="true">{ItemIcon && <ItemIcon size={16} />}</span>
                        <span>{item.shortTitle || item.title}</span>
                      </PageHandler>
                    );
                  })}
                </div>
              ))}
            </motion.nav>
          </>
        )}
      </AnimatePresence>

      {searchModal}
      <div className="today-shell__main">{children}</div>
    </div>
  );
};

export default TopBarV2;
